import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import menuService from "@services/menuService";
import { getHashtagsAPI } from "@modules/apis/hashtags";
import { LIMIT_CHAR } from "@constants";
import compareVersions, { VERSION_DAM_891_HASHTAG_SUGGESTIONS } from "@cores/version";

const DEBOUNCE_MS = 300;
const PAGE_LIMIT = 20;

/**
 * 태그 입력값으로 기존 해시태그를 전방 일치 조회해 자동완성 추천 목록을 만든다.
 * 댓글 멘션(@)의 사용자 추천과 같은 흐름이다 — 입력 → 지연 조회 → 목록 표시 → 선택.
 *
 * - 한 글자 이상 입력해야 조회한다 (빈 입력은 추천하지 않는다).
 * - 늦게 도착한 이전 검색어의 응답은 버린다 (요청 순번 비교).
 * - 목록 끝에 닿으면 nextToken 으로 다음 페이지를 이어 붙인다.
 * - 이미 추가된 태그는 앞뒤 공백을 무시하고 추천에서 뺀다 (TagsInput 중복 검증과 같은 기준).
 * - 해시태그 조회 API 가 없는 스테이지(VERSION_DAM_891_HASHTAG_SUGGESTIONS 미만)에서는 조회하지 않는다 — 추천 없이 자유 입력만 남는다.
 * - 조회 대상 프로젝트는 URL(콘솔·프로젝트 설정 경로)을 우선하고, 없으면 현재 선택된 프로젝트를 쓴다.
 *
 * @param {{ enabled: boolean, keyword: string, excludedTags?: string[] }} params
 * @returns {{ suggestions: string[], pending: boolean, fetchNext: () => void }}
 */
const useHashtagSuggestions = ({ enabled, keyword, excludedTags = [] }) => {
    const stage = useSelector(({ stage }) => stage);
    const currentProjectId = useSelector(({ project }) => project?.id);
    const { pathname } = useLocation();
    const projectId = menuService.parse(pathname).projectId ?? currentProjectId;

    const [results, setResults] = useState([]);
    const [pending, setPending] = useState(false);
    const nextTokenRef = useRef(null);
    const requestSeqRef = useRef(0);

    const searchValue = (keyword ?? "").trim();
    const isSupported = compareVersions(stage?.version, VERSION_DAM_891_HASHTAG_SUGGESTIONS) >= 0;
    const canSearch =
        enabled &&
        isSupported &&
        !!projectId &&
        !!stage?.endpoint &&
        !!searchValue &&
        searchValue.length <= LIMIT_CHAR.tag;

    const fetchPage = useCallback(
        async (value, nextToken) => {
            const seq = ++requestSeqRef.current;
            setPending(true);
            try {
                const params = { value, limit: PAGE_LIMIT };
                if (nextToken) params.nextToken = nextToken;
                const { data } = await getHashtagsAPI(stage.endpoint, projectId, params);
                if (seq !== requestSeqRef.current) return;
                const page = data?.results ?? [];
                setResults((prev) => (nextToken ? [...prev, ...page] : page));
                nextTokenRef.current = data?.nextToken ?? null;
            } catch (error) {
                if (seq !== requestSeqRef.current) return;
                console.error(error);
                if (!nextToken) setResults([]);
                nextTokenRef.current = null;
            } finally {
                if (seq === requestSeqRef.current) setPending(false);
            }
        },
        [stage?.endpoint, projectId],
    );

    useEffect(() => {
        if (!canSearch) {
            requestSeqRef.current += 1;
            nextTokenRef.current = null;
            setResults([]);
            setPending(false);
            return undefined;
        }
        const timer = setTimeout(() => {
            nextTokenRef.current = null;
            fetchPage(searchValue, null);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [canSearch, searchValue, fetchPage]);

    // 언마운트 시 진행 중 요청의 응답을 버린다 (닫힌 다이얼로그에 state 를 갱신하지 않도록)
    useEffect(
        () => () => {
            requestSeqRef.current += 1;
        },
        [],
    );

    const fetchNext = useCallback(() => {
        if (!canSearch || pending || !nextTokenRef.current) return;
        fetchPage(searchValue, nextTokenRef.current);
    }, [canSearch, pending, searchValue, fetchPage]);

    const excludedKey = excludedTags.map((tag) => String(tag ?? "").trim()).join("\u0000");
    const suggestions = useMemo(() => {
        const excluded = new Set(excludedKey ? excludedKey.split("\u0000") : []);
        return [...new Set(results)].filter((tag) => !excluded.has(String(tag).trim()));
    }, [results, excludedKey]);

    return { suggestions, pending, fetchNext };
};

export default useHashtagSuggestions;
